import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import {
  paintSelectionMaskOverlay,
  selectionHasPixels,
  traceSelectionContours,
} from '../../lib/imageEditorSelection';
import { editorPathToSvgD } from '../../lib/imageEditorPath';

/**
 * Selection overlay: true mask shape (fill + boundary), not AABB.
 * Draft previews keep live rect/ellipse/lasso/poly/path feedback.
 *
 * @param {{
 *   selection: import('../../lib/imageEditorSelection').SelectionMask|null,
 *   draft?: {
 *     type: string,
 *     x0?: number,
 *     y0?: number,
 *     x1?: number,
 *     y1?: number,
 *     points?: Array<{x:number,y:number}>,
 *     hover?: {x:number,y:number},
 *     closed?: boolean,
 *     path?: import('../../lib/imageEditorPath').EditorPath,
 *     snapClose?: boolean,
 *   }|null,
 *   zoom: number,
 *   canvasWidth: number,
 *   canvasHeight: number,
 * }} props
 */
export default function ImageEditorSelectionOverlay({
  selection,
  draft = null,
  zoom,
  canvasWidth,
  canvasHeight,
}) {
  const maskCanvasRef = useRef(null);
  const hasSelection = selectionHasPixels(selection);

  useLayoutEffect(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas || !hasSelection || !selection) return;
    if (canvas.width !== selection.width || canvas.height !== selection.height) {
      canvas.width = selection.width;
      canvas.height = selection.height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    paintSelectionMaskOverlay(ctx, selection);
  }, [selection, hasSelection]);

  const contours = useMemo(
    () => (hasSelection && selection ? traceSelectionContours(selection) : []),
    [selection, hasSelection],
  );

  const pathD = useMemo(() => {
    if (!draft || draft.type !== 'path' || !draft.path) return '';
    return editorPathToSvgD(draft.path, {
      hover: draft.path.closed ? null : draft.hover,
    });
  }, [draft]);

  if (!canvasWidth || !canvasHeight) return null;

  const z = Math.max(zoom, 0.25);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 3,
      }}
    >
      {hasSelection ? (
        <Box
          component="canvas"
          ref={maskCanvasRef}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: canvasWidth * zoom,
            height: canvasHeight * zoom,
            imageRendering: 'pixelated',
            pointerEvents: 'none',
          }}
        />
      ) : null}

      {contours.length ? (
        <Box
          component="svg"
          width={canvasWidth * zoom}
          height={canvasHeight * zoom}
          sx={{ position: 'absolute', inset: 0, overflow: 'visible' }}
        >
          {contours.map((ring, index) => (
            <polyline
              key={`contour-${index}`}
              fill="none"
              stroke="rgba(255,255,255,0.95)"
              strokeWidth={Math.max(1, 1.25 / z)}
              strokeDasharray={`${4 / z} ${3 / z}`}
              points={ring.map((p) => `${p.x * zoom},${p.y * zoom}`).join(' ')}
            />
          ))}
        </Box>
      ) : null}

      {draft && (draft.type === 'rect' || draft.type === 'ellipse' || draft.type === 'crop') ? (
        <Box
          sx={{
            position: 'absolute',
            left: Math.min(draft.x0, draft.x1) * zoom,
            top: Math.min(draft.y0, draft.y1) * zoom,
            width: Math.abs(draft.x1 - draft.x0) * zoom,
            height: Math.abs(draft.y1 - draft.y0) * zoom,
            borderRadius: draft.type === 'ellipse' ? '50%' : 0,
            outline:
              draft.type === 'crop'
                ? '2px solid rgba(255,183,77,0.95)'
                : '1px dashed rgba(255,255,255,0.9)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.45)',
            bgcolor:
              draft.type === 'crop'
                ? 'rgba(255,183,77,0.12)'
                : 'rgba(0,229,255,0.08)',
          }}
        />
      ) : null}

      {draft &&
      (draft.type === 'lasso' ||
        draft.type === 'poly' ||
        draft.type === 'magnetic') &&
      Array.isArray(draft.points) &&
      draft.points.length > 0 ? (
        <Box
          component="svg"
          width={canvasWidth * zoom}
          height={canvasHeight * zoom}
          sx={{ position: 'absolute', inset: 0, overflow: 'visible' }}
        >
          <polyline
            fill={
              draft.type === 'poly' && draft.closed
                ? 'rgba(0,229,255,0.12)'
                : 'none'
            }
            stroke="rgba(255,255,255,0.95)"
            strokeWidth={1}
            strokeDasharray="4 3"
            points={[
              ...draft.points,
              ...(draft.hover && !draft.closed ? [draft.hover] : []),
            ]
              .map((p) => `${p.x * zoom},${p.y * zoom}`)
              .join(' ')}
          />
          {draft.type === 'poly' && draft.points.length >= 3 ? (
            <circle
              cx={draft.points[0].x * zoom}
              cy={draft.points[0].y * zoom}
              r={Math.max(
                draft.snapClose ? 7 : 5,
                (draft.snapClose ? 11 : 8) / z,
              )}
              fill={
                draft.snapClose
                  ? 'rgba(0,229,255,0.45)'
                  : 'rgba(0,229,255,0.25)'
              }
              stroke="#00e5ff"
              strokeWidth={draft.snapClose ? 2 : 1.5}
            />
          ) : null}
          {draft.points.map((p, i) => (
            <circle
              key={`pt-${i}`}
              cx={p.x * zoom}
              cy={p.y * zoom}
              r={Math.max(2, 3 / z)}
              fill={draft.type === 'magnetic' ? '#ffab40' : '#00e5ff'}
              stroke="#111"
              strokeWidth={1}
            />
          ))}
        </Box>
      ) : null}

      {draft && draft.type === 'path' && draft.path?.anchors?.length ? (
        <Box
          component="svg"
          width={canvasWidth * zoom}
          height={canvasHeight * zoom}
          viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
          sx={{ position: 'absolute', inset: 0, overflow: 'visible' }}
        >
          {pathD ? (
            <path
              d={pathD}
              fill={draft.path.closed ? 'rgba(0,229,255,0.12)' : 'none'}
              stroke="rgba(255,255,255,0.95)"
              strokeWidth={1 / z}
              strokeDasharray={
                draft.path.closed ? undefined : `${4 / z} ${3 / z}`
              }
            />
          ) : null}
          {draft.path.anchors.length >= 3 && !draft.path.closed ? (
            <circle
              cx={draft.path.anchors[0].x}
              cy={draft.path.anchors[0].y}
              r={(draft.snapClose ? 7 : 5) / z}
              fill={
                draft.snapClose
                  ? 'rgba(0,229,255,0.45)'
                  : 'rgba(0,229,255,0.25)'
              }
              stroke="#00e5ff"
              strokeWidth={(draft.snapClose ? 2 : 1.5) / z}
            />
          ) : null}
          {draft.path.anchors.map((a, i) => (
            <g key={`anchor-${i}`}>
              {(a.ox !== 0 || a.oy !== 0) && (
                <>
                  <line
                    x1={a.x}
                    y1={a.y}
                    x2={a.x + a.ox}
                    y2={a.y + a.oy}
                    stroke="rgba(255,183,77,0.9)"
                    strokeWidth={1 / z}
                  />
                  <circle
                    cx={a.x + a.ox}
                    cy={a.y + a.oy}
                    r={3 / z}
                    fill="#ffb74d"
                    stroke="#111"
                    strokeWidth={1 / z}
                  />
                </>
              )}
              {(a.ix !== 0 || a.iy !== 0) && (
                <>
                  <line
                    x1={a.x}
                    y1={a.y}
                    x2={a.x + a.ix}
                    y2={a.y + a.iy}
                    stroke="rgba(255,183,77,0.9)"
                    strokeWidth={1 / z}
                  />
                  <circle
                    cx={a.x + a.ix}
                    cy={a.y + a.iy}
                    r={3 / z}
                    fill="#ffb74d"
                    stroke="#111"
                    strokeWidth={1 / z}
                  />
                </>
              )}
              <circle
                cx={a.x}
                cy={a.y}
                r={3.5 / z}
                fill="#00e5ff"
                stroke="#111"
                strokeWidth={1 / z}
              />
            </g>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
